// client/src/Components/OverlayEditor/overlayColors.content.ts
// Overlay content colors: what the stream canvas draws for the user. Spec §13 keeps the overlay canvas
// out of the redesign, so these are content, not app chrome: overlay template defaults, the preset
// palette of the color picker and (from Plan 2.3 Task 3) the canvas-only editor guides. The module is
// exempt from the raw-color guard through src/Themes/raw-color-content.json. Konva and the canvas 2D
// API cannot read CSS variables, so the values stay literal. App chrome never imports this module.

/** Default fill of new text, shape and clock overlays. */
export const OVERLAY_WHITE = "#ffffff";
/** Default outline, shadow and clock-hand color of new overlays. */
export const OVERLAY_BLACK = "#000000";
/** Default seconds hand of a new analog clock. */
export const OVERLAY_RED = "#EB144C";

/** The color picker's preset swatches, stored without the leading hash (SelectColorPopover adds it). */
export const COLOR_OPTIONS = [
  { value: "FFFFFF" },
  { value: "000000" },
  { value: "FF6900" },
  { value: "FCB900" },
  { value: "7BDCB5" },
  { value: "00D084" },
  { value: "00AFB9" },
  { value: "8ED1FC" },
  { value: "0693E3" },
  { value: "ABB8C3" },
  { value: "EB144C" },
  { value: "F78DA7" },
  { value: "9900EF" },
  { value: "9F86C0" },
  { value: "transparent" },
];

/** Stops of a new gradient text stroke (magenta, cyan, magenta, yellow). */
export const TEXT_STROKE_GRADIENT_STOPS: readonly (string | number)[] = [
  0,
  "#ff00ff",
  0.2,
  "#00ffff",
  0.7,
  "#ff00ff",
  1,
  "#ffff00",
];
/** Stops of a gradient text fill the overlay does not define: white to black. */
export const TEXT_FILL_GRADIENT_STOPS: readonly (string | number)[] = [
  0,
  OVERLAY_WHITE,
  1,
  OVERLAY_BLACK,
];
/** The analog clock's seconds hand when the overlay has no color (pure red). */
export const OVERLAY_CLOCK_SECONDS_FALLBACK = "#ff0000";

/** Snap guide line and the drag-select box on the editor canvas (editor-only, never streamed). */
export const EDITOR_GUIDE_STROKE = "rgb(0, 161, 255)";
export const EDITOR_MARQUEE_FILL = "rgba(0, 161, 255, 0.3)";
/** Corner handles of a custom rectangle (light blue). */
export const EDITOR_HANDLE_FILL = "#add8e6";

/** A rainbow hue for the audio visualizer: the same string the renderer always drew. */
export const overlayHsl = (hue: number, saturation = 100, lightness = 50) =>
  `hsl(${hue}, ${saturation}%, ${lightness}%)`;
/** A particle color with the particle's opacity. */
export const overlayRgba = (
  r: number | string,
  g: number | string,
  b: number | string,
  alpha: number
) => `rgba(${r}, ${g}, ${b}, ${alpha})`;
/** Adds an alpha channel to a computed browser color string (the rgb form getComputedStyle returns). */
export const overlayRgbToRgba = (rgb: string, alpha: number) =>
  rgb.replace(/rgb\(/, "rgba(").replace(/\)/, `, ${alpha})`);

/** Stops of a new gradient text fill in the text editor: white to a dark warm grey. */
export const TEXT_FILL_GRADIENT_PRESET: readonly (string | number)[] = [0, OVERLAY_WHITE, 0.5, "#504141"];
